import * as React from 'react';

export type InteractiveHandle = string;

interface InteractiveHandlesContextType {
    stack: InteractiveHandle[];
    nearestHandle?: string;
    handles: { [handleId: string]: string };
}

const defaultHandlesContext: InteractiveHandlesContextType = {
    stack: [],
    handles: {},
};

export const InteractiveHandlesContext = React.createContext<InteractiveHandlesContextType>({
    stack: [],
    handles: {},
});

const usedHandles = new Set<InteractiveHandle>();

/**
 * Create a unique handle to identify an interactive element and connect other Interactive.View to its hover/focus state.
 * Most of the time, you don't need to use createInteractiveHandle directly and you should use the .Child.
 *
 * createInteractiveHandle should be used when nesting multiple interactive views and a view is depending on an ancestor hovering state.
 *
 * Example:
 *
 *      const handleId = Interactive.createInteractiveHandle('my-handle');
 *
 *      function MyComponent() {
 *          return (
 *              <Interactive.View handle={handleId} hoverStyle={{ backgroundColor: 'red' }}>
 *                  <Interactive.Text descendantOf={handleId} hoverStyle={{ opacity: 0 }}>
 *                      Text invisible when parent is hovered
 *                  </Interactive.Text>
 *                  <Interactive.Text descendantOf={handleId} focusStyle={{ opacity: 0 }}>
 *                      Text invisible when parent is focused
 *                  </Interactive.Text>
 *              </Interactive.View>
 *          );
 *      }
 */
export function createInteractiveHandle(handleId: string): InteractiveHandle {
    if (process.env.NODE_ENV === 'development') {
        if (usedHandles.has(handleId)) {
            throw new Error(`Interactive handle "${handleId}" already defined`);
        }

        usedHandles.add(handleId);
    }

    return handleId;
}

/**
 * Reset the stack of used handles.
 * This component should be used when using a React.Portal where the component stacking will not matched the DOM stacking.
 */
export function ResetInteractiveHandles(props: { children: React.ReactNode }) {
    return (
        <InteractiveHandlesContext.Provider value={defaultHandlesContext}>
            {props.children}
        </InteractiveHandlesContext.Provider>
    );
}
